import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import Button from 'components/Button/Button';
import { useModalRegistration } from 'views/ModalRegistryProvider';
import SelectionBox from './SelectionBox';
import DelegateSelect from './DelegateSelect';
import { DelegateOption } from './DelegatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  faRotateRight,
  faUserCheck,
  faFileShield
} from '@fortawesome/pro-duotone-svg-icons';
import Signature from '../Signature';
import headerCheckerboard from 'assets/logo/header-checkerboard-background-checks.svg';
import moment, { Moment } from 'moment';

const useStyles = createUseStyles((theme: Theme) => ({
  '@keyframes overlayIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  },
  '@keyframes panelIn': {
    from: { opacity: 0, transform: 'scale(0.94) translateY(24px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0)' }
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '7vh',
    background: 'rgba(23, 25, 35, 0.18)',
    animation: '$overlayIn 0.15s ease-out'
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    width: 640,
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: '86vh',
    background: 'white',
    borderRadius: 14,
    border: '1px solid #ededed',
    boxShadow: '0 16px 70px rgba(0, 0, 0, 0.2), 0 3px 12px rgba(0, 0, 0, 0.08)',
    animation: '$panelIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    overflow: 'hidden'
  },
  cover: {
    position: 'relative',
    flexShrink: 0,
    height: 84,
    background: `
      url(${headerCheckerboard}) left top / auto 100% no-repeat,
      linear-gradient(135deg, #f4f7ff, #eef3ff)
    `,
    borderBottom: '1px solid #ededed'
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'white',
    border: '1px solid #ededed',
    cursor: 'pointer',
    color: '#74777f',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  medallionRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: -34,
    zIndex: 1
  },
  medallion: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 68,
    height: 68,
    borderRadius: '50%',
    background: 'white',
    border: '1px solid #ededed',
    boxShadow: '0 4px 14px rgba(11, 87, 255, 0.14)',
    color: '#0b57ff',
    fontSize: 26
  },
  titleBlock: {
    padding: '12px 28px 18px',
    textAlign: 'center'
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  subtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#74777f'
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 28px 28px'
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea2ad',
    margin: '8px 0 12px'
  },
  selectionBoxes: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridGap: 20,
    marginBottom: 18
  },
  delegateSelectWrap: {
    marginBottom: 20
  },
  optionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gridGap: 14,
    padding: '10px 16px',
    marginBottom: 10,
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 10,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.15s, border-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  optionRowChecked: {
    extend: 'optionRow',
    borderColor: '#0b57ff',
    background: 'rgba(11, 87, 255, 0.05)'
  },
  checkbox: {
    width: 18,
    height: 18,
    marginTop: 1,
    flexShrink: 0,
    accentColor: '#0b57ff',
    cursor: 'pointer'
  },
  optionLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.primaryBlack
  },
  optionHint: {
    fontSize: 13,
    color: '#74777f',
    marginTop: 2,
    lineHeight: 1.35
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 18
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: '#74777f',
    marginBottom: 7
  },
  labelHint: {
    fontWeight: 400,
    color: '#9ea2ad'
  },
  dateInput: {
    boxSizing: 'border-box',
    minHeight: 40,
    padding: '0 14px',
    fontSize: 14,
    color: theme.colors.primaryBlack,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)',
      outline: 'none'
    }
  },
  signatureRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: 20,
    marginTop: 16,
    marginBottom: 4
  },
  totalInline: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gridGap: 4,
    padding: '14px 16px',
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 10
  },
  signatureFill: {
    flexShrink: 0
  },
  costCard: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: 4,
    padding: '14px 16px',
    marginTop: 4,
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 10
  },
  costRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between'
  },
  costLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: '#74777f'
  },
  costValue: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  costHint: {
    fontSize: 12.5,
    color: '#0b8f3b'
  },
  errorBanner: {
    padding: '12px 14px',
    marginBottom: 14,
    fontSize: 13,
    color: '#b3261e',
    background: '#fef1f2',
    border: '1px solid #fadcdf',
    borderRadius: 8
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: 10,
    padding: '18px 28px',
    borderTop: '1px solid #ededed'
  },
  footerRight: {
    display: 'flex',
    alignItems: 'center',
    gridGap: 10
  }
}));

export type CheckType = 'initial' | 'renewal' | 'onlyDBS';

// Two pages: "select" (pick a delegate, if not preselected, then the check
// type) and "renewal" (only reached for renewals — extra confirmations).
type Stage = 'select' | 'renewal';

type Props = {
  isOpen: boolean;
  onRequest: (
    delegateUUID: string,
    requestDBS: boolean,
    checkType: CheckType,
    idDate?: Moment
  ) => void;
  dbsCost: number; // Cost of the DBS & Background check combined
  dbsOnlyCost: number; // Cost of DBS only
  checkCost: number;
  renewalCost: number;
  companyName: string;
  onClose: () => void;
  checksLeft: number;
  dbsLeft: number;
  renewalsLeft: number;
  managerName: string;
  // When provided the delegate picker is skipped and the wizard opens on the
  // check-type stage (e.g. launched from a delegate's profile page).
  delegate?: DelegateOption;
};

export default function RequestBackgroundCheckModal({
  isOpen,
  onRequest,
  companyName,
  managerName,
  renewalCost,
  dbsCost,
  dbsOnlyCost,
  checkCost,
  checksLeft,
  dbsLeft,
  renewalsLeft,
  onClose,
  delegate
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  useModalRegistration(isOpen);

  const [selected, setSelected] = React.useState<DelegateOption | undefined>(
    delegate
  );
  const [stage, setStage] = React.useState<Stage>('select');
  const [checked, setChecked] = React.useState(false);
  const [checkType, setCheckType] = React.useState<CheckType>(
    delegate?.hasActiveBackgroundCheck ? 'renewal' : 'initial'
  );
  const [complianceChecks, setComplicanceChecks] = React.useState({
    continuousEmployment: false,
    identity: false,
    upToDate: false
  });
  const [IDDate, setIDDate] = React.useState<Moment | undefined>(undefined);
  const [errorText, setErrorText] = React.useState<string | undefined>(
    undefined
  );

  const resetForm = (forDelegate?: DelegateOption) => {
    setSelected(forDelegate);
    setStage('select');
    setCheckType(forDelegate?.hasActiveBackgroundCheck ? 'renewal' : 'initial');
    setChecked(false);
    setComplicanceChecks({
      continuousEmployment: false,
      identity: false,
      upToDate: false
    });
    setIDDate(undefined);
    setErrorText(undefined);
  };

  // Reset the wizard whenever it opens
  React.useEffect(() => {
    if (!isOpen) return;
    resetForm(delegate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // People with an existing check can only request a renewal
  const hasInitial = selected?.hasActiveBackgroundCheck ?? false;
  const userName = selected ? `${selected.firstName} ${selected.lastName}` : '';

  const onPickDelegate = (picked: DelegateOption) => {
    setSelected(picked);
    // Keep the user's chosen check type; only force renewal when the picked
    // delegate already has a check, since Initial isn't available to them.
    if (picked.hasActiveBackgroundCheck && checkType === 'initial') {
      setCheckType('renewal');
    }
    // Renewal confirmations reference the specific person, so clear them.
    setComplicanceChecks({
      continuousEmployment: false,
      identity: false,
      upToDate: false
    });
    setIDDate(undefined);
    setErrorText(undefined);
  };

  const goBack = () => {
    setErrorText(undefined);
    if (stage === 'renewal') {
      setStage('select');
    }
  };

  const formatter = Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  });

  const costings: Record<CheckType, number> = {
    initial: checked ? dbsCost : checkCost,
    renewal: renewalCost,
    onlyDBS: dbsOnlyCost
  };

  let cost = formatter.format(costings[checkType] / 100);
  let checksLeftText = '';

  // Cost is free when there are checks remaining
  if (checkType === 'initial') {
    if (checked && dbsLeft > 0) {
      checksLeftText = `${dbsLeft} free initial checks (+dbs) remaining`;
      cost = formatter.format(0);
    } else if (checksLeft > 0) {
      checksLeftText = `${checksLeft} free initial checks remaining`;
      cost = formatter.format(0);
    }
  }
  if (checkType === 'renewal' && renewalsLeft > 0) {
    checksLeftText = `${renewalsLeft} free renewal checks remaining`;
    cost = formatter.format(0);
  }

  const submit = () => {
    if (!selected) return;

    if (checkType === 'renewal') {
      if (
        !complianceChecks.continuousEmployment ||
        !complianceChecks.identity ||
        !complianceChecks.upToDate
      ) {
        setErrorText('Please confirm all compliance checks before proceeding');
        return;
      }
      if (!IDDate || moment(IDDate).isBefore(moment())) {
        setErrorText('Please enter a valid ID expiry date');
        return;
      }
    }
    // if only DBS is selected, the dbs checkbox must be treated as checked
    onRequest(
      selected.uuid,
      checked || checkType === 'onlyDBS',
      checkType,
      IDDate
    );
  };

  const costCard = (
    <div className={classes.costCard}>
      <div className={classes.costRow}>
        <span className={classes.costLabel}>Total</span>
        <span className={classes.costValue}>{cost}</span>
      </div>
      {checksLeftText && <div className={classes.costHint}>{checksLeftText}</div>}
    </div>
  );

  const canPickDelegate = !delegate;
  const canGoBack = stage === 'renewal';

  const title =
    stage === 'renewal' ? 'Renewal details' : 'Request background check';
  const subtitle =
    stage === 'renewal'
      ? `Confirm ${userName}'s renewal`
      : selected
      ? `Choose a check for ${userName}`
      : 'Select a delegate to get started';

  return (
    <div className={classes.overlay} onMouseDown={onClose}>
      <div
        className={classes.panel}
        onMouseDown={(evt) => evt.stopPropagation()}
      >
        <div className={classes.cover}>
          <div className={classes.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </div>
        </div>
        <div className={classes.medallionRow}>
          <div className={classes.medallion}>
            <FontAwesomeIcon icon={faShieldCheck} />
          </div>
        </div>
        <div className={classes.titleBlock}>
          <div className={classes.title}>{title}</div>
          <div className={classes.subtitle}>{subtitle}</div>
        </div>

        <div className={classes.body}>
          {errorText && <div className={classes.errorBanner}>{errorText}</div>}

          {stage === 'select' && (
            <>
              {canPickDelegate && (
                <>
                  <div className={classes.sectionLabel}>Delegate</div>
                  <div className={classes.delegateSelectWrap}>
                    <DelegateSelect
                      value={selected}
                      onChange={onPickDelegate}
                    />
                  </div>
                </>
              )}

              <div className={classes.sectionLabel}>Check type</div>
              <div className={classes.selectionBoxes}>
                <SelectionBox
                  disabled={hasInitial}
                  selected={checkType === 'initial'}
                  icon={faUserCheck}
                  title={'Initial Background check'}
                  subtitle="Request a 5 year background check"
                  boxText="Initial check"
                  onClick={() => setCheckType('initial')}
                />
                <SelectionBox
                  selected={checkType === 'renewal'}
                  icon={faRotateRight}
                  title={'Renewal'}
                  subtitle="Request a 3 year background check renewal"
                  boxText="Renewal"
                  onClick={() => setCheckType('renewal')}
                />
                <SelectionBox
                  selected={checkType === 'onlyDBS'}
                  icon={faFileShield}
                  title={'DBS check'}
                  subtitle="Request a DBS check only"
                  boxText="DBS check"
                  onClick={() => {
                    setCheckType('onlyDBS');
                    setChecked(true);
                  }}
                />
              </div>

              {checkType === 'initial' && (
                <div
                  className={
                    checked ? classes.optionRowChecked : classes.optionRow
                  }
                  onClick={() => setChecked(!checked)}
                >
                  <input
                    type="checkbox"
                    className={classes.checkbox}
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                  />
                  <div>
                    <div className={classes.optionLabel}>
                      Include DBS check{' '}
                      {formatter.format((dbsCost - checkCost) / 100)}
                    </div>
                    <div className={classes.optionHint}>
                      Also request a DBS check for this candidate
                    </div>
                  </div>
                </div>
              )}

              {checkType !== 'renewal' && costCard}
            </>
          )}

          {stage === 'renewal' && (
            <>
              <div className={classes.sectionLabel}>ID document</div>
              <div className={classes.field}>
                <span className={classes.label}>
                  ID expiry date{' '}
                  <span className={classes.labelHint}>
                    — passport, driving licence, etc.
                  </span>
                </span>
                <input
                  type="date"
                  className={classes.dateInput}
                  onChange={(e) => {
                    setIDDate(moment.utc(e.target.value, 'YYYY-MM-DD'));
                  }}
                />
              </div>

              <div className={classes.sectionLabel}>Confirmations</div>
              <div
                className={
                  complianceChecks.continuousEmployment
                    ? classes.optionRowChecked
                    : classes.optionRow
                }
                onClick={() =>
                  setComplicanceChecks({
                    ...complianceChecks,
                    continuousEmployment: !complianceChecks.continuousEmployment
                  })
                }
              >
                <input
                  type="checkbox"
                  className={classes.checkbox}
                  checked={complianceChecks.continuousEmployment}
                  onChange={(e) =>
                    setComplicanceChecks({
                      ...complianceChecks,
                      continuousEmployment: e.target.checked
                    })
                  }
                />
                <div>
                  <div className={classes.optionLabel}>
                    {userName} has remained in continuous employment
                  </div>
                  <div className={classes.optionHint}>
                    No unexplained gaps of more than 28 days with {companyName}.
                  </div>
                </div>
              </div>

              <div
                className={
                  complianceChecks.identity
                    ? classes.optionRowChecked
                    : classes.optionRow
                }
                onClick={() =>
                  setComplicanceChecks({
                    ...complianceChecks,
                    identity: !complianceChecks.identity
                  })
                }
              >
                <input
                  type="checkbox"
                  className={classes.checkbox}
                  checked={complianceChecks.identity}
                  onChange={(e) =>
                    setComplicanceChecks({
                      ...complianceChecks,
                      identity: e.target.checked
                    })
                  }
                />
                <div>
                  <div className={classes.optionLabel}>
                    {userName}'s identity has not changed
                  </div>
                  <div className={classes.optionHint}>
                    Or where it has, this has been validated (e.g. marriage
                    certificate, deed poll).
                  </div>
                </div>
              </div>

              <div
                className={
                  complianceChecks.upToDate
                    ? classes.optionRowChecked
                    : classes.optionRow
                }
                onClick={() =>
                  setComplicanceChecks({
                    ...complianceChecks,
                    upToDate: !complianceChecks.upToDate
                  })
                }
              >
                <input
                  type="checkbox"
                  className={classes.checkbox}
                  checked={complianceChecks.upToDate}
                  onChange={(e) =>
                    setComplicanceChecks({
                      ...complianceChecks,
                      upToDate: e.target.checked
                    })
                  }
                />
                <div>
                  <div className={classes.optionLabel}>
                    {userName}'s background check is up to date
                  </div>
                  <div className={classes.optionHint}>
                    Their 5 year background check remains up to date.
                  </div>
                </div>
              </div>

              <div className={classes.signatureRow}>
                <div className={classes.signatureFill}>
                  <Signature name={managerName} />
                </div>
                <div className={classes.totalInline}>
                  <div className={classes.costRow}>
                    <span className={classes.costLabel}>Total</span>
                    <span className={classes.costValue}>{cost}</span>
                  </div>
                  {checksLeftText && (
                    <div className={classes.costHint}>{checksLeftText}</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className={classes.footer}>
          <div>
            {canGoBack && (
              <Button archetype="flat" onClick={goBack}>
                Back
              </Button>
            )}
          </div>
          <div className={classes.footerRight}>
            <Button archetype="flat" onClick={onClose}>
              Cancel
            </Button>
            {stage === 'select' && checkType === 'renewal' ? (
              <Button
                archetype="flatPrimary"
                disabled={!selected}
                onClick={() => {
                  setErrorText(undefined);
                  setStage('renewal');
                }}
              >
                Next
              </Button>
            ) : stage === 'select' ? (
              <Button
                archetype="flatPrimary"
                disabled={!selected}
                onClick={submit}
              >
                Request check
              </Button>
            ) : stage === 'renewal' ? (
              <Button
                archetype="flatPrimary"
                disabled={
                  !complianceChecks.upToDate ||
                  !complianceChecks.identity ||
                  !complianceChecks.continuousEmployment
                }
                onClick={submit}
              >
                Request check
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
